import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const FALLBACK_SUGGESTIONS = [
  'Idee für den Morgenkreis',
  'Rhythmusgefühl bei U3-Kindern fördern',
  'Bewegungslied zum Thema Tiere',
  'Gitarre im Kita-Alltag einsetzen',
];

const SUGGESTION_TEMPLATES = [
  (course: string) => `Tipps zu "${course}"`,
  (course: string) => `Idee passend zu "${course}"`,
  (course: string) => `Was erwartet mich in "${course}"?`,
  (course: string) => `Wie setze ich "${course}" in der Praxis um?`,
];

type CourseEnrollmentSuggestion = {
  courses: { title: string | null } | null;
};

/** Simple daily seed so suggestions rotate each day */
function dailySeed(): number {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

/** Deterministic shuffle based on a numeric seed */
function seededShuffle<T>(arr: T[], seed: number): T[] {
  const a = [...arr];
  let s = seed;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    const j = s % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function useMikoSuggestions() {
  const [suggestions, setSuggestions] = useState<string[]>(FALLBACK_SUGGESTIONS.slice(0, 3));

  useEffect(() => {
    const load = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;
        const user = session.user;

        const { data: enrollments } = await supabase
          .from('course_enrollments')
          .select('courses!inner(title)')
          .eq('user_id', user.id)
          .limit(30);

        if (!enrollments || enrollments.length === 0) return;

        // Kurse, die generell nicht in Vorschlägen erscheinen sollen
        const EXCLUDED_COURSES = ['detlev jöcker'];

        // Saisonale Filter: Monate, in denen ein Thema NICHT vorgeschlagen werden soll
        // Monat: 1 = Januar ... 12 = Dezember
        const month = new Date().getMonth() + 1;
        const SEASONAL_KEYWORDS: { keywords: string[]; allowedMonths: number[] }[] = [
          // Weihnachten/Advent: nur Okt–Dez
          { keywords: ['weihnacht', 'advent', 'nikolaus', 'christkind'], allowedMonths: [10, 11, 12] },
          // Winter: Nov–Feb
          { keywords: ['winter', 'schnee'], allowedMonths: [11, 12, 1, 2] },
          // Frühling/Ostern: Mär–Mai
          { keywords: ['frühling', 'fruehling', 'ostern', 'osterzeit'], allowedMonths: [3, 4, 5] },
          // Sommer: Jun–Aug
          { keywords: ['sommer', 'urlaub', 'strand'], allowedMonths: [6, 7, 8] },
          // Herbst/Erntedank/Halloween: Sep–Nov
          { keywords: ['herbst', 'erntedank', 'halloween', 'laterne', 'st. martin', 'sankt martin'], allowedMonths: [9, 10, 11] },
          // Karneval/Fasching: Jan–Feb
          { keywords: ['karneval', 'fasching', 'fastnacht'], allowedMonths: [1, 2] },
        ];

        const isSeasonallyAppropriate = (title: string): boolean => {
          const lower = title.toLowerCase();
          for (const { keywords, allowedMonths } of SEASONAL_KEYWORDS) {
            if (keywords.some((kw) => lower.includes(kw))) {
              return allowedMonths.includes(month);
            }
          }
          return true;
        };

        const courseTitles = ((enrollments || []) as CourseEnrollmentSuggestion[])
          .map((enrollment) => enrollment.courses?.title)
          .filter((title): title is string => Boolean(title))
          .filter((title) => {
            const lower = title.toLowerCase();
            if (EXCLUDED_COURSES.some((excluded) => lower.includes(excluded))) return false;
            return isSeasonallyAppropriate(title);
          });

        if (courseTitles.length === 0) return;

        const seed = dailySeed();
        const shuffledCourses = seededShuffle(courseTitles, seed);
        const picked = shuffledCourses.slice(0, 2);

        const shuffledTemplates = seededShuffle(SUGGESTION_TEMPLATES, seed + 1);
        const personalized = picked.map((course, i) =>
          shuffledTemplates[i % shuffledTemplates.length](course)
        );

        const generalPool = seededShuffle(FALLBACK_SUGGESTIONS, seed + 2);
        const general = generalPool.slice(0, 1);

        setSuggestions([...personalized, ...general]);
      } catch (err) {
        console.error('Error loading suggestions:', err);
      }
    };

    load();
  }, []);

  return suggestions;
}
